import { useCallback, useEffect, useRef } from 'react';
import { FormField } from '../components/FormField';
import { SelectField } from '../components/SelectField';
import { TextInput } from '../components/TextInput';
import { Button } from '../components/Button';
import {
  KNOWN_ACTION_KINDS,
  MAX_STEPS,
  emptyStep,
  reorderSteps,
  type FieldErrors,
  type StepDraft,
} from './journeyAuthoring';
import styles from './StepEditor.module.css';

const ACTION_OPTIONS = KNOWN_ACTION_KINDS.map((kind) => ({
  value: kind,
  label: kind.charAt(0).toUpperCase() + kind.slice(1),
}));

export interface StepEditorProps {
  steps: StepDraft[];
  onChange: (steps: StepDraft[]) => void;
  errors: FieldErrors;
  /** Parent's polite live region — announces add/remove/move. */
  announce: (message: string) => void;
}

/**
 * Ordered, keyboard-operable step editor (#133). Steps are an `<ol>` of
 * `<fieldset>`s; reordering uses Move up/Move down buttons (omitted at the
 * boundaries) rather than drag-and-drop. Focus follows the mutated step: after
 * add → the new step's action; after remove → the next/previous step (or Add);
 * after move → the moved step's active move control (or its action at a boundary).
 */
export function StepEditor({ steps, onChange, errors, announce }: StepEditorProps) {
  const addRef = useRef<HTMLButtonElement>(null);
  // DOM id of the control to focus after the next render (steps change), or 'add'.
  const pendingFocus = useRef<string | null>(null);

  useEffect(() => {
    const target = pendingFocus.current;
    if (!target) return;
    pendingFocus.current = null;
    if (target === 'add') {
      addRef.current?.focus();
      return;
    }
    document.getElementById(target)?.focus();
  }, [steps]);

  const updateStep = useCallback(
    (index: number, patch: Partial<StepDraft>) => {
      onChange(steps.map((step, i) => (i === index ? { ...step, ...patch } : step)));
    },
    [steps, onChange],
  );

  const addStep = useCallback(() => {
    if (steps.length >= MAX_STEPS) return;
    const step = emptyStep();
    onChange([...steps, step]);
    pendingFocus.current = `${step.key}-action`;
    announce(`Step ${steps.length + 1} added.`);
  }, [steps, onChange, announce]);

  const removeStep = useCallback(
    (index: number) => {
      const next = steps.filter((_, i) => i !== index);
      onChange(next);
      if (next.length === 0) {
        pendingFocus.current = 'add';
      } else {
        const focusIndex = index < next.length ? index : index - 1;
        pendingFocus.current = `${next[focusIndex]!.key}-action`;
      }
      announce(`Step ${index + 1} removed.`);
    },
    [steps, onChange, announce],
  );

  const moveStep = useCallback(
    (index: number, direction: -1 | 1) => {
      const to = index + direction;
      if (to < 0 || to >= steps.length) return;
      const moved = steps[index]!;
      onChange(reorderSteps(steps, index, to));
      const canStillMove = direction > 0 ? to < steps.length - 1 : to > 0;
      pendingFocus.current = canStillMove
        ? `${moved.key}-move${direction > 0 ? 'Down' : 'Up'}`
        : `${moved.key}-action`;
      announce(`Step ${index + 1} moved to position ${to + 1}.`);
    },
    [steps, onChange, announce],
  );

  const atCap = steps.length >= MAX_STEPS;

  return (
    <div className={styles.editor}>
      <div className={styles.header}>
        <h2 className={styles.heading}>Steps</h2>
        <p className={styles.hint}>
          Optional — the ordered actions a shopper takes. Add, reorder, or remove steps.
        </p>
      </div>

      {steps.length === 0 ? (
        <p className={styles.empty}>No steps yet.</p>
      ) : (
        <ol className={styles.list} aria-label="Journey steps">
          {steps.map((step, index) => {
            const n = index + 1;
            return (
              <li key={step.key} className={styles.item}>
                <fieldset className={styles.step}>
                  <legend className={styles.legend}>Step {n}</legend>

                  <SelectField
                    label="Action"
                    id={`${step.key}-action`}
                    aria-label={`Step ${n} action`}
                    value={step.actionKind}
                    onChange={(value) => updateStep(index, { actionKind: value })}
                    options={ACTION_OPTIONS}
                    {...(errors[`step-${index}-actionKind`]
                      ? { error: errors[`step-${index}-actionKind`] }
                      : {})}
                  />

                  <FormField
                    label="Description"
                    id={`${step.key}-description`}
                    {...(errors[`step-${index}-description`]
                      ? { error: errors[`step-${index}-description`] }
                      : {})}
                  >
                    {({ controlProps }) => (
                      <textarea
                        {...controlProps}
                        className={styles.textarea}
                        aria-label={`Step ${n} description`}
                        rows={2}
                        maxLength={500}
                        value={step.description}
                        onChange={(e) => updateStep(index, { description: e.target.value })}
                      />
                    )}
                  </FormField>

                  <FormField
                    label="Target path"
                    id={`${step.key}-targetPath`}
                    helpText="Optional. A root-relative path (/checkout) or a URL on the verified host."
                    {...(errors[`step-${index}-targetPath`]
                      ? { error: errors[`step-${index}-targetPath`] }
                      : {})}
                  >
                    {({ controlProps }) => (
                      <TextInput
                        {...controlProps}
                        type="text"
                        aria-label={`Step ${n} target path`}
                        value={step.targetPath}
                        onChange={(e) => updateStep(index, { targetPath: e.target.value })}
                      />
                    )}
                  </FormField>

                  <div className={styles.rowActions}>
                    {index > 0 ? (
                      <Button
                        id={`${step.key}-moveUp`}
                        variant="ghost"
                        aria-label={`Move step ${n} up`}
                        onClick={() => moveStep(index, -1)}
                      >
                        Move up
                      </Button>
                    ) : null}
                    {index < steps.length - 1 ? (
                      <Button
                        id={`${step.key}-moveDown`}
                        variant="ghost"
                        aria-label={`Move step ${n} down`}
                        onClick={() => moveStep(index, 1)}
                      >
                        Move down
                      </Button>
                    ) : null}
                    <Button
                      id={`${step.key}-remove`}
                      variant="danger"
                      aria-label={`Remove step ${n}`}
                      onClick={() => removeStep(index)}
                    >
                      Remove
                    </Button>
                  </div>
                </fieldset>
              </li>
            );
          })}
        </ol>
      )}

      <div className={styles.addRow}>
        <Button
          ref={addRef}
          variant="secondary"
          onClick={addStep}
          aria-disabled={atCap || undefined}
          {...(atCap ? { 'aria-describedby': 'step-cap-hint' } : {})}
        >
          Add step
        </Button>
        {atCap ? (
          <span id="step-cap-hint" className={styles.capHint}>
            A journey can have at most {MAX_STEPS} steps.
          </span>
        ) : null}
      </div>
    </div>
  );
}
